/* eslint-disable @typescript-eslint/no-empty-function */
import { DeleteOutlineOutlined, ExpandMore } from '@mui/icons-material'
import { Grid } from '@mui/material'
import { unwrapResult } from '@reduxjs/toolkit'
import React, { ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CONSTANTS, { PAGES_ROUTES } from '../../../Constants'
import { PERMISSIONS, PERMISSION_TYPE, checkPermission } from '../../../Helpers/Permission'
import { convertBooleanIntoYesAndNo, getValueByKey, isValidArray } from '../../../Helpers/Util'
import updateTagMetaData from '../../../Helpers/updateTagMetaData'
import { RoleConfirmationModal } from '../../../Models'
import { useAppDispatch, useAppSelector } from '../../../Store/Hooks'
import { showAlert } from '../../../Store/Slice/alert.slice'
import { getDataSource, selectDataSource } from '../../../Store/Slice/asset-definition.slice'
import {
  changeAttributeStatus,
  deleteAttribute,
  getAttributeService,
} from '../../../Store/Slice/attribute.slice'
import RoleConfirmation from '../../Dialogs/RoleConfirmation'
import ChipButton from '../../Shared/ChipButton'
import MUIBox from '../../Shared/MUI-Box'
import { EditIcon } from '../../Shared/MUI-Drawer/MUI-Drawer.svgIcons'

const REQUIRED_COLOR = { color: '#D4351C' }
const HIGHLIGHT_TEXT_STYLE = { fontWeight: 'bold', color: '#3E3F40', display: 'inline-block' }

const getDefaultDialogProps = (): RoleConfirmationModal => ({
  heading: '',
  description: '',
  color: 'primary',
  open: false,
})

const getPartnersFromArray = (partnersArr: any[]) => {
  if (!isValidArray(partnersArr)) {
    return CONSTANTS.DEFAULT_TEXT
  }

  return partnersArr.join(', ')
}

const getLabelArray = (attribute: any) =>
  attribute?.attributeRef?.map((item: any) => item?.label) || []

const formatBooleanValue = (value: any) => {
  if (typeof value === 'boolean') {
    return convertBooleanIntoYesAndNo(value)
  }

  return value ?? CONSTANTS.DEFAULT_TEXT
}

const getTitleValue = (value: any) => {
  if (typeof value !== 'string') {
    return ''
  }

  return value.length > 40 ? value : ''
}

const TextValueRow = ({ label, value, required = false }: any) => {
  const hasValue = value && value !== 'null'
  const displayValue = hasValue ? value : 'NULL'

  return (
    <Grid container>
      <Grid item xs={6} md={3}>
        <div className='sub-heading-6 view__role-list  text-left col34'>
          {label}
          {required && <span style={REQUIRED_COLOR}>*</span>}
        </div>
      </Grid>
      <Grid item xs={6} md={9}>
        <div
          className='sub-heading-6 view__role-list  text-left col36 word-wrap'
          title={getTitleValue(displayValue)}
        >
          {displayValue}
        </div>
      </Grid>
    </Grid>
  )
}

const KeyValueRow = ({ label, value, required = false }: any): any => {
  return (
    <Grid container>
      <Grid item xs={6} md={3}>
        <div className='sub-heading-6 view__role-list  text-left col34'>
          {label}
          {required && <span style={REQUIRED_COLOR}>*</span>}
        </div>
      </Grid>
      <Grid item xs={6} md={9}>
        <div className='sub-heading-6 view__role-list  text-left col36 word-wrap'>{value}</div>
      </Grid>
    </Grid>
  )
}

const StatusRow = ({ status }: any) => {
  const statusProps: any = {
    ACTIVE: { type: 'success', label: 'Active' },
    INACTIVE: { type: 'secondary', label: 'In Active' },
  }
  const currentStatus = statusProps[status]

  return (
    <Grid container>
      <Grid item xs={6} md={3}>
        <div className='sub-heading-6 view__role-list  text-left col34'>
          Status<span style={REQUIRED_COLOR}>*</span>
        </div>
      </Grid>
      <Grid item xs={6} md={9}>
        <div className='sub-heading-6 view__role-list  text-left col36 word-wrap'>
          {currentStatus && (
            <ChipButton
              type={currentStatus.type}
              label={currentStatus.label}
              style={{ padding: '0rem 0.5rem' }}
            />
          )}
        </div>
      </Grid>
    </Grid>
  )
}

const PlatformExamplesSection = ({ attribute }: any) => {
  const examples = attribute?.example || {}
  const exampleKeys = Object.keys(examples)

  if (exampleKeys.length === 0) {
    return (
      <Grid container>
        <Grid item xs={6} md={3}>
          <div className='sub-heading-6 view__role-list  text-left col34'>
            Platform Examples (e.g., WEB, APP, TV, B2B, etc.)
          </div>
        </Grid>
        <Grid item xs={6} md={9}>
          <div className='sub-heading-6 view__role-list  text-left col36 word-wrap'>NULL</div>
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container>
      <Grid item xs={12} md={12}>
        <div className='sub-heading-6 view__role-list  text-left col34'>
          Platform Examples (e.g., WEB, APP, TV, B2B, etc.)
        </div>
      </Grid>
      {exampleKeys.map((key: string) => (
        <React.Fragment key={key}>
          <Grid item xs={6} md={3}>
            <div className='sub-heading-6 view__role-list  text-left col34'>{key}</div>
          </Grid>
          <Grid item xs={6} md={9}>
            <div
              className='sub-heading-6 view__role-list  text-left col36 word-wrap'
              title={getTitleValue(examples?.[key] || '')}
            >
              {examples?.[key] || 'NULL'}
            </div>
          </Grid>
        </React.Fragment>
      ))}
    </Grid>
  )
}

const getDeleteDialogProps = (
  attribute: any,
  handleClose: any,
  handleDelete: any,
): RoleConfirmationModal => {
  if (attribute?.isAssigned) {
    return {
      heading: CONSTANTS.DELETE_ATTRIBUTE_TITLE,
      description: (
        <div>
          <p>
            Can not remove attribute, assigned to existing asset type and asset has been created.
          </p>
        </div>
      ),
      color: 'warning',
      Icon: 'DeleteRoleWarIcon',
      open: true,
      iconColor: 'warning',
      cancelButtonLabel: null,
      successButtonLabel: 'Okay',
      handleSuccess: handleClose,
    }
  }

  return {
    heading: CONSTANTS.DELETE_ATTRIBUTE_TITLE,
    description: (
      <div>
        <p>
          {CONSTANTS.DELETE_ATTRIBUTE_DESCRIPTION1}
          <strong style={HIGHLIGHT_TEXT_STYLE}>{attribute?.fieldName}</strong>
          {CONSTANTS.DELETE_ATTRIBUTE_DESCRIPTION2}
        </p>
      </div>
    ),
    color: 'error',
    Icon: 'DeleteRoleIcon',
    open: true,
    iconColor: 'error',
    successButtonLabel: 'Delete',
    cancelButtonLabel: 'Cancel',
    handleSuccess: handleDelete,
  }
}

const getActiveDialogProps = (attribute: any, handleStatusChange: any): RoleConfirmationModal => ({
  heading: CONSTANTS.ATTRIBUTE_STATUS_ACTIVE_TITLE,
  description: (
    <div>
      <p>
        Are you sure you want to activate the previously deactivated attribute{' '}
        <strong style={HIGHLIGHT_TEXT_STYLE}>{attribute?.fieldName}</strong>?
      </p>
    </div>
  ),
  color: 'primary',
  Icon: 'ActiveIcon',
  open: true,
  iconColor: 'success',
  successButtonLabel: 'Activate',
  cancelButtonLabel: 'Cancel',
  handleSuccess: () => handleStatusChange('ACTIVE'),
})

const getInactiveDialogProps = (
  attribute: any,
  handleClose: any,
  handleStatusChange: any,
): RoleConfirmationModal => {
  if (attribute?.isAssigned) {
    return {
      heading: CONSTANTS.ATTRIBUTE_STATUS_DEACTIVE_TITLE,
      description: (
        <div>
          <p>
            Can not deactivate attribute, assigned to existing asset type and asset has been created
          </p>
        </div>
      ),
      color: 'warning',
      Icon: 'DeleteRoleWarIcon',
      open: true,
      iconColor: 'warning',
      cancelButtonLabel: null,
      successButtonLabel: 'Okay',
      handleSuccess: handleClose,
    }
  }

  return {
    heading: CONSTANTS.ATTRIBUTE_STATUS_DEACTIVE_TITLE,
    description: (
      <div>
        <p>
          Are you sure you want to deactivate attribute{' '}
          <strong style={HIGHLIGHT_TEXT_STYLE}>{attribute?.fieldName}</strong>?
        </p>
      </div>
    ),
    color: 'error',
    Icon: 'InactiveIcon',
    open: true,
    iconColor: 'error',
    successButtonLabel: 'Deactivate',
    cancelButtonLabel: 'Cancel',
    handleSuccess: () => handleStatusChange('INACTIVE'),
  }
}

const getDialogPropsByType = (
  type: string,
  attribute: any,
  handleClose: any,
  handleDelete: any,
  handleStatusChange: any,
): RoleConfirmationModal | null => {
  if (type === 'delete') {
    return getDeleteDialogProps(attribute, handleClose, handleDelete)
  }

  if (type === 'status-active') {
    return getActiveDialogProps(attribute, handleStatusChange)
  }

  if (type === 'status-inactive') {
    return getInactiveDialogProps(attribute, handleClose, handleStatusChange)
  }

  return null
}

const handleStatusChangeAction = async ({
  dispatch,
  attributeId,
  type,
  handleClose,
  setDialogProps,
  getAttributeData,
}: any) => {
  const result = await dispatch(
    changeAttributeStatus({ attributeId: attributeId || '', status: type }) as any,
  )
  const unwrappableRes = unwrapResult(result)

  if (unwrappableRes?.error || !unwrappableRes) {
    handleClose()

    if (unwrappableRes?.error?.responseCode === 422) {
      setDialogProps({
        heading: CONSTANTS.CHANGE_ATTRIBUTE_STATUS,
        description: unwrappableRes.error.messaage,
        color: 'warning',
        Icon: 'DeleteRoleWarIcon',
        open: true,
        iconColor: 'warning',
        cancelButtonLabel: 'Okay',
      })
      return
    }

    dispatch(showAlert([true, unwrappableRes?.error?.message, 'error']))
    return
  }

  dispatch(showAlert([true, unwrappableRes.message, 'success']))
  getAttributeData()
  handleClose()
}

const handleDeleteAction = async ({
  dispatch,
  attributeId,
  handleClose,
  setDialogProps,
  navigate,
}: any) => {
  const result = await dispatch(deleteAttribute(attributeId as string) as any)
  const unwrappableRes = unwrapResult(result)

  if (unwrappableRes?.error || !unwrappableRes) {
    if (unwrappableRes?.error?.blitzCode === CONSTANTS.BLITZ_ERROR_CODE_BLOCK_DELETE_ATTRIBUTE) {
      handleClose()
      setDialogProps({
        heading: CONSTANTS.DELETE_ATTRIBUTE_TITLE,
        description: `${CONSTANTS.ATTEIBUTE_DELETE_BLOCK_MESSAGE} `,
        color: 'warning',
        Icon: 'DeleteRoleWarIcon',
        open: true,
        iconColor: 'warning',
        cancelButtonLabel: null,
        successButtonLabel: 'Okay',
        handleSuccess: handleClose,
      })
      return
    }

    dispatch(showAlert([true, unwrappableRes?.error?.message || unwrappableRes?.message, 'error']))
    return
  }

  dispatch(showAlert([true, unwrappableRes.message, 'success']))
  handleClose()
  navigate('/metamaster/attribute-repo')
}

const AttributeInfoContent = ({
  accessWrite,
  attribute,
  masterDataListStore,
  openDialog,
  handleEdit,
}: any) => {
  const isComplexType = attribute?.fieldType === 'COMPLEXTYPE'
  const shouldShowOptions =
    !isComplexType &&
    (attribute?.fieldType !== 'SELECT' || attribute?.dataType !== 'BOOLEAN') &&
    attribute?.master === ''
  const shouldShowPartners = typeof attribute?.isB2B === 'boolean' && attribute?.isB2B
  const masterValue = attribute?.master
    ? getValueByKey(attribute?.master, masterDataListStore) || CONSTANTS.DEFAULT_TEXT
    : attribute?.master === ''
    ? 'Custom Option'
    : CONSTANTS.DEFAULT_TEXT

  const optionsValue =
    isValidArray(attribute?.options) && attribute?.options?.[0] !== ''
      ? attribute?.options.join(',')
      : CONSTANTS.DEFAULT_TEXT

  const metaAttributeLabels = getPartnersFromArray(getLabelArray(attribute))
  const partnerNames = getPartnersFromArray(attribute?.affiliatePartner ?? [])

  return (
    <div>
      <div className='heading-6 d-flex text-left rolebox__header-padding'>
        <div className='d-flex align-items-center'>Attribute Information</div>
        {accessWrite && (
          <div className='ml-auto d-flex'>
            <div className='mr-1'>
              <ChipButton
                type={attribute?.status === 'ACTIVE' ? 'success' : 'secondary'}
                dropdown
                Icon={ExpandMore}
                label={
                  attribute?.status === 'ACTIVE'
                    ? CONSTANTS.STATUS_LABEL.ACTIVE
                    : CONSTANTS.STATUS_LABEL.INACTIVE
                }
                suffix
                options={CONSTANTS.STATUS_LIST}
                action={openDialog}
                value={attribute?.status ?? 'ACTIVE'}
              />
            </div>
            <div
              className='mr-1'
              onClick={() => openDialog('delete')}
              data-testid='delete__chip-btn'
            >
              <ChipButton type='danger' Icon={DeleteOutlineOutlined} label='Delete' />
            </div>
            <div onClick={handleEdit} data-testid='edit__chip-btn'>
              <ChipButton type='primary' Icon={EditIcon as any} label='Edit' />
            </div>
          </div>
        )}
      </div>
      <div className={'hr'}></div>
      <div className=''>
        <TextValueRow label='Field Name' value={attribute?.fieldName} required />
        <TextValueRow label='Label' value={attribute?.label} required />
        <TextValueRow label='Field Type' value={attribute?.fieldType} required />
        <TextValueRow label='Data Type' value={attribute?.dataType} required />
        <TextValueRow label='ES Field' value={attribute?.esField} required />
        <KeyValueRow label='Required' value={formatBooleanValue(attribute?.isRequired)} required />
        <StatusRow status={attribute?.status} />
        <KeyValueRow
          label='Is Multilingual'
          value={formatBooleanValue(attribute?.isMultilingual)}
        />

        {!isComplexType && (
          <TextValueRow label='Placeholder' value={attribute?.placeholder} required />
        )}

        <KeyValueRow label='B2B' value={formatBooleanValue(attribute?.isB2B)} required />
        <KeyValueRow label='Add More' value={formatBooleanValue(attribute?.addMore)} />
        <KeyValueRow label='Is Bulk Update' value={formatBooleanValue(attribute?.isBulkUpdate)} />
        <KeyValueRow
          label='Is Advanced Search'
          value={formatBooleanValue(attribute?.isAdvancedSearch)}
        />
        <KeyValueRow
          label='Include in Open Search'
          value={formatBooleanValue(attribute?.includeInOpenSearch || 'Yes')}
        />

        <TextValueRow label='Description' value={attribute?.description} required />
        <TextValueRow label='Additional Properties' value={attribute?.additionalProperties} />

        {!isComplexType && <TextValueRow label='Default Value' value={attribute?.defaultValue} />}

        {!isComplexType && (
          <KeyValueRow
            label='Multiselect'
            value={formatBooleanValue(attribute?.isMultiple)}
            required
          />
        )}

        {!isComplexType && <KeyValueRow label='Master' value={masterValue} />}
        {shouldShowOptions && <KeyValueRow label='Options' value={optionsValue} />}
        {isComplexType && (
          <KeyValueRow label='Meta Attributes' value={metaAttributeLabels || 'NULL'} />
        )}

        {shouldShowPartners && <KeyValueRow label='Partner`s name' value={partnerNames} required />}

        <TextValueRow label='Sample Value in CSV' value={attribute?.sampleValue} />
        <KeyValueRow label='Is EMA Field' value={formatBooleanValue(attribute?.isEMA)} />
        <PlatformExamplesSection attribute={attribute} />
      </div>
    </div>
  )
}

const AttributeInformation = ({ attributeId = '', setAttributeLabel = () => {} }: any) => {
  const accessWrite = checkPermission(PERMISSIONS.META_MASTER_ATTRIBUTE_REPO, [
    PERMISSION_TYPE.CAN_WRITE,
  ])

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const masterDataListStore: any[] = useAppSelector(selectDataSource)
  const [attribute, setAttribute] = useState<any>({})
  const [dialogProps, setDialogProps] = React.useState<RoleConfirmationModal>(
    getDefaultDialogProps(),
  )

  useEffect(() => {
    if (attribute?.label) {
      updateTagMetaData({ title: `Attribute: ${attribute?.label as string}` })
    }
    return () => {
      updateTagMetaData({})
    }
  }, [attribute?.fieldName])

  const getAttributeData = async () => {
    const { payload = {} } = await dispatch(getAttributeService(attributeId as string) as any)
    const { data = {} } = payload
    setAttributeLabel(data?.label || '')
    const clone = { ...data }
    clone.additionalProperties = JSON.stringify(clone?.additionalProperties) || ''
    setAttribute(clone)
  }

  useEffect(() => {
    dispatch(getDataSource() as any)
    if (attributeId) {
      getAttributeData()
    }
  }, [])

  const handleClose = () => {
    setDialogProps(getDefaultDialogProps())
  }

  const handleStatusChange = async (type: string) => {
    await handleStatusChangeAction({
      dispatch,
      attributeId,
      type,
      handleClose,
      setDialogProps,
      getAttributeData,
    })
  }

  const handleDelete = async () => {
    await handleDeleteAction({ dispatch, attributeId, handleClose, setDialogProps, navigate })
  }

  const openDialog = (type: string) => {
    const nextDialogProps = getDialogPropsByType(
      type,
      attribute,
      handleClose,
      handleDelete,
      handleStatusChange,
    )

    if (nextDialogProps) {
      setDialogProps(nextDialogProps)
    }
  }

  const handleEdit = () => {
    navigate(
      `/${PAGES_ROUTES.MetaMaster}/${PAGES_ROUTES.AttributeRepository}/edit/${attribute._id}`,
    )
  }

  return (
    <>
      <MUIBox>
        <AttributeInfoContent
          accessWrite={accessWrite}
          attribute={attribute}
          masterDataListStore={masterDataListStore}
          openDialog={openDialog}
          handleEdit={handleEdit}
        />
      </MUIBox>
      <RoleConfirmation {...dialogProps} handleClose={handleClose} />
    </>
  )
}

export default React.memo(AttributeInformation)
